import React, { useState, useRef, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import styled from '@emotion/styled';
import MapBackground from './MapBackground';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  position: relative;
`;

const Card = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
  border-radius: 24px;
  padding: 32px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-sizing: border-box;
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 8px;
  font-size: 36px;
  font-weight: 800;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 32px;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  margin-bottom: 16px;
  background-color: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: white;
  font-size: 16px;
  box-sizing: border-box;
  display: block;
  
  &:focus {
    outline: none;
    border-color: #4ade80;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(120deg, #4ade80, #22d3ee);
  border: none;
  border-radius: 12px;
  color: #0f172a;
  font-weight: bold;
  cursor: pointer;
  margin: 16px 0;
`;

const ErrorMsg = styled.div`
  background-color: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
`;

const SuccessMsg = styled.div`
  background-color: rgba(74, 222, 128, 0.15);
  color: #86efac;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
`;

const Link = styled.a`
  color: #4ade80;
  cursor: pointer;
  text-decoration: none;
  margin-left: 5px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 24px;
  color: rgba(255, 255, 255, 0.7);
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 100px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  margin: 0 8px;
  width: calc(50% - 16px);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 0;
  color: rgba(255, 255, 255, 0.5);
  
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
  
  &::before {
    margin-right: 16px;
  }
  
  &::after {
    margin-left: 16px;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background-color: rgba(23, 43, 36, 0.95);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.12);
  position: relative;
`;

const ModalHeader = styled.h3`
  margin-top: 0;
  color: #4ade80;
  font-size: 18px;
  margin-bottom: 16px;
`;

const ModalBody = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
  
  p {
    margin-bottom: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 20px;
  cursor: pointer;
  
  &:hover {
    color: white;
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 12px 0;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 1.5;
`;

const Checkbox = styled.input`
  margin-top: 3px;
  accent-color: #4ade80;
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
`;

const TosLink = styled.span`
  color: #4ade80;
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: #6ee7a0;
  }
`;

const TosContent = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  line-height: 1.7;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
  
  h4 {
    color: #4ade80;
    margin: 16px 0 8px;
    font-size: 14px;
  }
  
  h4:first-of-type {
    margin-top: 0;
  }
  
  p {
    margin-bottom: 10px;
  }
  
  ul {
    margin: 8px 0 12px;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 4px;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(74, 222, 128, 0.4);
    border-radius: 3px;
  }
`;

const Auth: React.FC = () => {
  // Store UI state
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot' | 'success'>('login');
  
  // Store form values directly using useRef to prevent rerender issues
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  
  // Store error and success messages
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Add state for form submission to prevent multiple submissions
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  
  // Get auth functions
  const { signIn, signUp } = useAuth();

  // Add state for modals
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  
  // Effect to handle cooldown timer
  React.useEffect(() => {
    if (cooldownTime <= 0) return;
    
    const timer = setTimeout(() => {
      setCooldownTime(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [cooldownTime]);

  // Function to open email client
  const openSupportEmail = () => {
    window.location.href = 'mailto:james.perrin@networkrail.co.uk?subject=JPNWR%20Maps%20Support';
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Check if already submitting
    if (isSubmitting) return;
    
    // Check cooldown if in signup mode
    if (mode === 'signup' && cooldownTime > 0) {
      setError(`Please wait ${cooldownTime} seconds before trying again.`);
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    // Get current values directly from refs
    const email = emailRef.current?.value || '';
    const password = passwordRef.current?.value || '';
    
    if (!email) {
      setError('Email is required');
      setIsSubmitting(false);
      return;
    }
    
    if (mode !== 'forgot' && !password) {
      setError('Password is required');
      setIsSubmitting(false);
      return;
    }
    
    if (mode === 'signup') {
      const confirmPassword = confirmPasswordRef.current?.value || '';
      if (!confirmPassword) {
        setError('Please confirm your password');
        setIsSubmitting(false);
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setIsSubmitting(false);
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        setIsSubmitting(false);
        return;
      }
      if (!tosAccepted) {
        setError('You must accept the Terms of Service to create an account');
        setIsSubmitting(false);
        return;
      }
    }
    
    try {
      if (mode === 'login') {
        await signIn(email, password);
      } else if (mode === 'signup') {
        // Create the user account
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
        });

        if (authError) throw authError;

        // Check if auth was successful but no user was returned
        if (!authData?.user?.id) {
          console.error('No user ID returned during signup:', authData);
          throw new Error('Account creation failed. The authentication service did not return a valid user ID.');
        }

        // Profile is auto-created by the database trigger (handle_new_user).
        // Just attempt an upsert as a safety net in case the trigger didn't fire.
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert({
              id: authData.user.id,
              email,
              is_admin: false,
              is_approved: false,
              created_at: new Date().toISOString()
            }, { onConflict: 'id' });

          if (profileError) {
            console.log('Profile upsert returned error (trigger likely handled it):', profileError);
          }

          setSuccessMessage('Your account has been created. Please check your email to confirm your sign up and wait for administrator approval.');
          setMode('success');
          await supabase.auth.signOut();
        } catch (err) {
          console.error('Profile creation error:', err);
          // Even if profile insert fails, the auth user was created and the
          // database trigger should have created the profile row.
          // Treat signup as successful.
          setSuccessMessage('Your account has been created. Please check your email to confirm your sign up and wait for administrator approval.');
          setMode('success');
          try { await supabase.auth.signOut(); } catch (_) {}
        }
      } else if (mode === 'forgot') {
        const redirectUrl = `${window.location.origin}/reset-password`;
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: redirectUrl,
        });
        
        if (error) throw error;
        
        setSuccessMessage('Password reset instructions have been sent to your email');
      }
    } catch (err) {
      console.error('Auth error:', err);
      
      // If we're in signup mode, check if the account might have been created anyway
      if (mode === 'signup') {
        try {
          const email = emailRef.current?.value || '';
          // Check if a user with this email exists in auth
          const { data: existingUser } = await supabase.auth.signInWithPassword({
            email: email,
            password: passwordRef.current?.value || '',
          });
          
          if (existingUser?.user) {
            console.log('User exists despite reported error:', existingUser);
            await supabase.auth.signOut();
            setSuccessMessage('Your account has been created. Please check your email to confirm your sign up and wait for administrator approval.');
            setMode('success');
            setIsSubmitting(false);
            return;
          }
        } catch (checkError) {
          console.error('Error checking for existing user:', checkError);
        }
      }
      
      let errorMessage = 'An error occurred';
      
      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Check for rate limiting errors
        if (errorMessage.includes('429') || 
            errorMessage.includes('Too Many Requests') ||
            errorMessage.includes('rate limit') ||
            errorMessage.includes('only request this after') ||
            errorMessage.includes('wait')) {
          
          // Extract the wait time if available
          const waitTimeMatch = errorMessage.match(/(\d+) seconds/);
          const waitTime = waitTimeMatch ? parseInt(waitTimeMatch[1]) : 30;
          
          setCooldownTime(waitTime);
          errorMessage = `Rate limit exceeded. Please wait ${waitTime} seconds before trying again.`;
        }
        
        // Replace generic Supabase error messages with more user-friendly ones
        if (errorMessage.includes('Failed to fetch')) {
          errorMessage = 'Could not connect to the authentication service. Please check your internet connection.';
        }
        
        if (errorMessage.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        }
        
        // If we see a profile error but the auth worked, consider it a success for signup
        if (mode === 'signup' && errorMessage.includes('Failed to create profile')) {
          try {
            // Check if user exists in auth database by trying to get the session
            const { data: session } = await supabase.auth.getSession();
            if (session?.session?.user) {
              console.log('User session exists, considering signup successful despite profile error');
              await supabase.auth.signOut();
              setSuccessMessage('Your account has been created. Please check your email to confirm your sign up and wait for administrator approval.');
              setMode('success');
              setIsSubmitting(false);
              return;
            }
          } catch (sessionError) {
            console.error('Error checking session:', sessionError);
          }
        }
      } else if (err && typeof err === 'object') {
        // Check if error object is empty
        if (Object.keys(err).length === 0) {
          console.log('Empty error object received');
          
          if (mode === 'signup') {
            errorMessage = 'Could not create account. The email may already be registered.';
          } else if (mode === 'login') {
            errorMessage = 'Login failed. Please check your credentials.';
          }
        } else {
          // Handle Supabase error format
          if ('message' in err && typeof err.message === 'string') {
            errorMessage = err.message;
          }
          if ('error_description' in err && typeof err.error_description === 'string') {
            errorMessage = err.error_description;
          }
          
          // Check for HTTP 429 (Too Many Requests) status code
          if ('status' in err && err.status === 429) {
            setCooldownTime(32); // Default to 32 seconds if not specified
            errorMessage = 'Too many signup attempts. Please wait 32 seconds before trying again.';
          }
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoginForm = () => (
    <>
      <Title>JPNWR Maps</Title>
      <Subtitle>A NWR Mapping system</Subtitle>
      
      <ActionContainer>
        <ActionButton type="button" onClick={() => setInfoModalOpen(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          Information
        </ActionButton>
        <ActionButton type="button" onClick={openSupportEmail}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          Support
        </ActionButton>
      </ActionContainer>
      
      <Divider>login with email</Divider>
      
      <form onSubmit={handleSubmit}>
        <Input 
          ref={emailRef}
          type="email" 
          placeholder="Email address" 
          defaultValue=""
        />
        <Input 
          ref={passwordRef}
          type="password" 
          placeholder="Password" 
          defaultValue=""
        />
        
        <div style={{ textAlign: 'right', marginBottom: '16px' }}>
          <Link onClick={() => setMode('forgot')}>Forgot Password?</Link>
        </div>
        
        {error && <ErrorMsg>{error}</ErrorMsg>}
        {cooldownTime > 0 && mode === 'signup' && (
          <ErrorMsg>Rate limit applied. Please wait {cooldownTime} seconds before trying again.</ErrorMsg>
        )}
        
        <Button type="submit" disabled={isSubmitting || (mode === 'signup' && cooldownTime > 0)}>
          {isSubmitting ? 'Processing...' : mode === 'login' ? 'Sign In' : 'Create Account'}
        </Button>
        
        <Footer>
          Don't have an account?
          <Link onClick={() => setMode('signup')}>Sign Up</Link>
        </Footer>
      </form>
      
      {/* Information Modal */}
      <Modal isOpen={infoModalOpen}>
        <ModalContent>
          <CloseButton onClick={() => setInfoModalOpen(false)}>×</CloseButton>
          <ModalHeader>About JPNWR Maps</ModalHeader>
          <ModalBody>
            <p>
              JPNWR Maps is an internal mapping system designed for Network Rail employees 
              to access geographical information related to railway infrastructure.
            </p>
            <p>
              This application provides authorized users with tools to view, manage, and 
              analyze geographical data within the Network Rail system.
            </p>
            <p>
              For assistance, please contact the support team using the "Support" button 
              or email james.perrin@networkrail.co.uk directly.
            </p>
          </ModalBody>
          <Button onClick={() => setInfoModalOpen(false)}>Close</Button>
        </ModalContent>
      </Modal>
    </>
  );

  const renderSignupForm = () => (
    <>
      <Title>JPNWR Maps</Title>
      <Subtitle>Register an account</Subtitle>
      
      <ActionContainer>
        <ActionButton type="button" onClick={() => setInfoModalOpen(true)}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          Information
        </ActionButton>
        <ActionButton type="button" onClick={openSupportEmail}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          Support
        </ActionButton>
      </ActionContainer>
      
      <Divider>register with email</Divider>
      
      <form onSubmit={handleSubmit}>
        <Input 
          ref={emailRef}
          type="email" 
          placeholder="Email address" 
          defaultValue=""
        />
        <Input 
          ref={passwordRef}
          type="password" 
          placeholder="Password" 
          defaultValue=""
        />
        <Input 
          ref={confirmPasswordRef}
          type="password" 
          placeholder="Confirm password" 
          defaultValue=""
        />
        
        <CheckboxContainer>
          <Checkbox 
            type="checkbox" 
            checked={tosAccepted} 
            onChange={(e) => setTosAccepted(e.target.checked)} 
          />
          <span>
            I agree to the{' '}
            <TosLink onClick={(e) => { e.preventDefault(); setTosModalOpen(true); }}>
              Terms of Service
            </TosLink>
          </span>
        </CheckboxContainer>
        
        {error && <ErrorMsg>{error}</ErrorMsg>}
        {cooldownTime > 0 && (
          <ErrorMsg>Rate limit applied. Please wait {cooldownTime} seconds before trying again.</ErrorMsg>
        )}
        
        <Button type="submit" disabled={isSubmitting || cooldownTime > 0}>
          {isSubmitting ? 'Processing...' : 'Create Account'}
        </Button>
        
        <Footer>
          Already have an account?
          <Link onClick={() => setMode('login')}>Log In</Link>
        </Footer>
      </form>
      
      {/* Information Modal */}
      <Modal isOpen={infoModalOpen}>
        <ModalContent>
          <CloseButton onClick={() => setInfoModalOpen(false)}>×</CloseButton>
          <ModalHeader>About JPNWR Maps</ModalHeader>
          <ModalBody>
            <p>
              JPNWR Maps is an internal mapping system designed for Network Rail employees 
              to access geographical information related to railway infrastructure.
            </p>
            <p>
              This application provides authorized users with tools to view, manage, and 
              analyze geographical data within the Network Rail system.
            </p>
            <p>
              For assistance, please contact the support team using the "Support" button 
              or email james.perrin@networkrail.co.uk directly.
            </p>
          </ModalBody>
          <Button onClick={() => setInfoModalOpen(false)}>Close</Button>
        </ModalContent>
      </Modal>
      
      {/* Terms of Service Modal */}
      <Modal isOpen={tosModalOpen}>
        <ModalContent>
          <CloseButton onClick={() => setTosModalOpen(false)}>×</CloseButton>
          <ModalHeader>Terms of Service</ModalHeader>
          <TosContent>
            <p><strong>Last updated: February 2026</strong></p>
            
            <h4>1. Acceptance of Terms</h4>
            <p>
              By creating an account and using JPNWR Maps ("the Application"), you agree to be 
              bound by these Terms of Service. If you do not agree to these terms, you must not 
              use the Application.
            </p>
            
            <h4>2. Authorised Use Only</h4>
            <p>
              This Application is intended exclusively for use by employees and authorised 
              contractors of Network Rail. Unauthorised access or use by individuals outside 
              of Network Rail is strictly prohibited. By registering, you confirm that you are 
              a current employee or authorised contractor of Network Rail.
            </p>
            
            <h4>3. Account Responsibilities</h4>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>All activity that occurs under your account</li>
              <li>Notifying the administrator immediately of any unauthorised use of your account</li>
            </ul>
            <p>
              Do not share your account credentials with any other person. Each user must 
              register their own account.
            </p>
            
            <h4>4. Acceptable Use</h4>
            <p>You agree not to:</p>
            <ul>
              <li>Share, distribute, or disclose any data from the Application to unauthorised third parties</li>
              <li>Use the Application for any purpose other than legitimate Network Rail business</li>
              <li>Attempt to access data or features beyond your authorised level</li>
              <li>Copy, reproduce, or extract data from the Application for external use</li>
              <li>Interfere with or disrupt the operation of the Application</li>
            </ul>
            
            <h4>5. Data &amp; Confidentiality</h4>
            <p>
              The geographical and infrastructure data contained within this Application is 
              proprietary and confidential. All data is provided for internal operational 
              purposes only and must be treated in accordance with Network Rail's data 
              handling and information security policies.
            </p>
            
            <h4>6. No Warranty</h4>
            <p>
              The Application is provided "as is" without warranty of any kind. While every 
              effort is made to ensure data accuracy, the Application should not be relied 
              upon as the sole source of information for safety-critical decisions. Always 
              verify information through official Network Rail channels.
            </p>
            
            <h4>7. Account Approval &amp; Termination</h4>
            <p>
              All new accounts require administrator approval before access is granted. 
              The administrator reserves the right to revoke access at any time, without 
              notice, for any violation of these terms or at the discretion of Network Rail.
            </p>
            
            <h4>8. Changes to Terms</h4>
            <p>
              These Terms of Service may be updated from time to time. Continued use of the 
              Application following any changes constitutes acceptance of the revised terms.
            </p>
            
            <h4>9. Contact</h4>
            <p>
              For questions regarding these terms or the Application, please contact 
              james.perrin@networkrail.co.uk.
            </p>
          </TosContent>
          <Button onClick={() => setTosModalOpen(false)} style={{ marginTop: '16px' }}>Close</Button>
        </ModalContent>
      </Modal>
    </>
  );

  const renderForgotPasswordForm = () => (
    <>
      <Title>JPNWR Maps</Title>
      <Subtitle>Reset your password</Subtitle>
      
      <form onSubmit={handleSubmit}>
        <Input 
          ref={emailRef}
          type="email" 
          placeholder="Email address" 
          defaultValue=""
        />
        
        {error && <ErrorMsg>{error}</ErrorMsg>}
        {successMessage && <SuccessMsg>{successMessage}</SuccessMsg>}
        
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Processing...' : 'Send Reset Instructions'}
        </Button>
        
        <Footer>
          <Link onClick={() => setMode('login')}>Back to Sign In</Link>
        </Footer>
      </form>
    </>
  );

  const renderSuccessMessage = () => (
    <>
      <Title>JPNWR Maps</Title>
      <Subtitle>Account Created</Subtitle>
      
      <SuccessMsg>{successMessage}</SuccessMsg>
      
      <Button onClick={() => setMode('login')}>Back to Sign In</Button>
    </>
  );

  return (
    <>
      <MapBackground interactive={true} />
      <Container>
        <Card>
          {mode === 'login' && renderLoginForm()}
          {mode === 'signup' && renderSignupForm()}
          {mode === 'forgot' && renderForgotPasswordForm()}
          {mode === 'success' && renderSuccessMessage()}
        </Card>
      </Container>
    </>
  );
};

export default Auth; 